import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

// useLoad(fn, deps): runs fn() on mount and when deps change. Returns
// { data, error, loading, reload, setData }. Stale responses are dropped.
export function useLoad(fn, deps = []) {
    const [state, setState] = useState({ data: null, error: null, loading: true });
    const seq = useRef(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const run = useCallback(fn, deps);

    const reload = useCallback(
        (opts = {}) => {
            const my = ++seq.current;
            if (!opts.silent) setState((s) => ({ ...s, loading: true, error: null }));
            return Promise.resolve()
                .then(run)
                .then((data) => {
                    if (my === seq.current) setState({ data, error: null, loading: false });
                    return data;
                })
                .catch((error) => {
                    if (my === seq.current) setState((s) => ({ ...s, error, loading: false }));
                });
        },
        [run]
    );

    useEffect(() => {
        reload();
    }, [reload]);

    const setData = useCallback((updater) => {
        setState((s) => ({ ...s, data: typeof updater === "function" ? updater(s.data) : updater }));
    }, []);

    return { ...state, reload, setData };
}

export function useDebounced(value, ms = 250) {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), ms);
        return () => clearTimeout(t);
    }, [value, ms]);
    return v;
}

export function useDocumentTitle(title) {
    useEffect(() => {
        document.title = title ? `${title} · Horus` : "Horus";
    }, [title]);
}

// localStorage-backed state for per-browser conveniences (saved views).
// Falls back to memory when storage is blocked.
export function useStoredState(key, initial) {
    const [value, setValue] = useState(() => {
        try {
            const raw = localStorage.getItem(key);
            return raw ? JSON.parse(raw) : initial;
        } catch (e) {
            return initial;
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (e) {
            // ignore
        }
    }, [key, value]);
    return [value, setValue];
}

// True when a keyboard event happens inside an editable field.
export const isTyping = (e) => {
    const t = e.target;
    if (!t) return false;
    const tag = (t.tagName || "").toLowerCase();
    return tag === "input" || tag === "textarea" || tag === "select" || t.isContentEditable;
};

// useQueryState keeps one list setting (page, filter, sort, search) in the URL
// query string, so "back" from a detail page restores the list where it was
// and a filtered list can be bookmarked. The type follows the default
// (number, boolean or string); the default value is left out of the URL.
// Updates replace the history entry instead of adding one per keystroke.
export function useQueryState(key, defaultValue) {
    const location = useLocation();
    const history = useHistory();
    const value = parseQuery(new URLSearchParams(location.search).get(key), defaultValue);
    const set = useCallback(
        (next) => {
            const loc = history.location; // current, also after an earlier set in the same tick
            const params = new URLSearchParams(loc.search);
            const prev = parseQuery(params.get(key), defaultValue);
            const v = typeof next === "function" ? next(prev) : next;
            if (v === defaultValue || v === undefined || v === null || v === "") params.delete(key);
            else params.set(key, typeof v === "boolean" ? (v ? "1" : "0") : String(v));
            const search = params.toString();
            if (search !== loc.search.replace(/^\?/, "")) history.replace({ pathname: loc.pathname, search, hash: loc.hash });
        },
        [history, key, defaultValue]
    );
    return [value, set];
}

export function parseQuery(raw, defaultValue) {
    if (raw === null) return defaultValue;
    if (typeof defaultValue === "number") {
        const n = Number(raw);
        return Number.isFinite(n) ? n : defaultValue;
    }
    if (typeof defaultValue === "boolean") return raw === "1" || raw === "true";
    return raw;
}

// useOnChange runs fn when deps change, but not on mount: used to go back to
// page 1 when a filter changes without wiping a page restored from the URL.
export function useOnChange(deps, fn) {
    const first = useRef(true);
    useEffect(() => {
        if (first.current) {
            first.current = false;
            return;
        }
        fn();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}

// Lists remember their last URL (page, filters, sort) for this browser tab,
// so "back to list" links on detail pages return to the same place.
const LIST_URL_KEY = "hz-list-url:";

export function useRememberListUrl() {
    const location = useLocation();
    useEffect(() => {
        try {
            sessionStorage.setItem(LIST_URL_KEY + location.pathname, location.search);
        } catch (e) {
            // storage blocked: links fall back to the bare list
        }
    }, [location.pathname, location.search]);
}

export function listUrl(pathname) {
    try {
        const search = sessionStorage.getItem(LIST_URL_KEY + pathname);
        return search ? pathname + (search.startsWith("?") ? search : "?" + search) : pathname;
    } catch (e) {
        return pathname;
    }
}
